import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../utils/apiUrl';
import { motion } from 'framer-motion';
import { 
  FiGift, FiSearch, FiFilter, FiMail, FiPhone, FiCalendar, 
  FiUser, FiDollarSign, FiMessageSquare, FiCheck, FiX, 
  FiSend, FiEdit2, FiTrash2, FiClock, FiPackage
} from 'react-icons/fi';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const API_URL = getApiUrl();

const statusConfig = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: FiClock },
  contacted: { label: 'Contacté', color: 'bg-blue-100 text-blue-800', icon: FiPhone },
  paid: { label: 'Payé', color: 'bg-green-100 text-green-800', icon: FiCheck },
  sent: { label: 'Envoyé', color: 'bg-purple-100 text-purple-800', icon: FiSend },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-800', icon: FiX }
};

const GiftCardRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sendingCard, setSendingCard] = useState(false);

  useEffect(() => {
    loadRequests();
  }, [statusFilter]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = statusFilter === 'all' 
        ? `${API_URL}/api/gift-card-requests`
        : `${API_URL}/api/gift-card-requests?status=${statusFilter}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setRequests(data.requests || []);
      }
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const sendGiftCard = async (requestId) => {
    if (!recipientEmail) {
      toast.error('Veuillez saisir l\'email du destinataire');
      return;
    }
    
    setSendingCard(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/gift-card-requests/${requestId}/send-card`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ recipient_email: recipientEmail })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`🎁 ${data.message}`);
        loadRequests();
        setSelectedRequest(prev => ({
          ...prev,
          status: 'sent',
          card_code: data.card_code,
          recipient_email_sent: recipientEmail,
          sent_at: new Date().toISOString()
        }));
        setRecipientEmail('');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setSendingCard(false);
    }
  };

  const updateStatus = async (requestId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/gift-card-requests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        toast.success('Statut mis à jour');
        loadRequests();
        if (selectedRequest?.id === requestId) {
          setSelectedRequest({ ...selectedRequest, status: newStatus });
        }
      }
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteRequest = async (requestId) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/gift-card-requests/${requestId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Demande supprimée');
        loadRequests();
        setShowDetailModal(false);
      }
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredRequests = requests.filter(req => {
    const search = searchTerm.toLowerCase();
    return (
      req.sender_name?.toLowerCase().includes(search) ||
      req.recipient_name?.toLowerCase().includes(search) ||
      req.sender_email?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    contacted: requests.filter(r => r.status === 'contacted').length,
    paid: requests.filter(r => r.status === 'paid').length,
    sent: requests.filter(r => r.status === 'sent').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FiGift className="text-pink-500" />
            Demandes de Cartes Cadeaux
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Gérez les demandes de cartes cadeaux des clients
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-gray-50 to-gray-100">
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-gray-500">Total</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
            <div className="text-sm text-yellow-600">En attente</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-700">{stats.contacted}</div>
            <div className="text-sm text-blue-600">Contactés</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-700">{stats.paid}</div>
            <div className="text-sm text-green-600">Payés</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-purple-700">{stats.sent}</div>
            <div className="text-sm text-purple-600">Envoyés</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher par nom ou email..."
                className="pl-10"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 px-4 rounded-md border border-gray-200"
            >
              <option value="all">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="contacted">Contacté</option>
              <option value="paid">Payé</option>
              <option value="sent">Envoyé</option>
              <option value="cancelled">Annulé</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Requests List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-pink-500 border-t-transparent" />
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FiGift className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p>Aucune demande de carte cadeau</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredRequests.map((request) => {
                const StatusIcon = statusConfig[request.status]?.icon || FiClock;
                return (
                  <motion.div
                    key={request.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedRequest(request);
                      setShowDetailModal(true);
                    }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                            <FiGift className="w-5 h-5 text-pink-600" />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">
                              {request.amount}€ - {request.prestation_type}
                            </div>
                            <div className="text-sm text-gray-500">
                              De: {request.sender_name} • Pour: {request.recipient_name}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <FiMail className="w-3 h-3" />
                            {request.sender_email}
                          </span>
                          {request.event_date && (
                            <span className="flex items-center gap-1">
                              <FiCalendar className="w-3 h-3" />
                              {new Date(request.event_date).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge className={statusConfig[request.status]?.color || 'bg-gray-100'}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig[request.status]?.label || request.status}
                        </Badge>
                        <span className="text-xs text-gray-400">
                          {formatDate(request.created_at)}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FiGift className="text-pink-500" />
              Détail de la demande
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between">
                <Badge className={`text-base ${statusConfig[selectedRequest.status]?.color}`}>
                  {statusConfig[selectedRequest.status]?.label}
                </Badge>
                <span className="text-sm text-gray-500">
                  {formatDate(selectedRequest.created_at)}
                </span>
              </div>

              {/* Amount */}
              <div className="text-center py-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl">
                <div className="text-4xl font-bold text-pink-600">{selectedRequest.amount}€</div>
                <div className="text-gray-500 mt-1">{selectedRequest.prestation_type}</div>
              </div>

              {/* Sender */}
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                  <FiUser className="w-4 h-4" /> Expéditeur
                </h4>
                <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                  <div className="font-medium">{selectedRequest.sender_name}</div>
                  <div className="text-sm text-gray-500 flex items-center gap-2">
                    <FiMail className="w-3 h-3" />
                    <a href={`mailto:${selectedRequest.sender_email}`} className="text-blue-600 hover:underline">
                      {selectedRequest.sender_email}
                    </a>
                  </div>
                  {selectedRequest.sender_phone && (
                    <div className="text-sm text-gray-500 flex items-center gap-2">
                      <FiPhone className="w-3 h-3" />
                      <a href={`tel:${selectedRequest.sender_phone}`} className="text-blue-600 hover:underline">
                        {selectedRequest.sender_phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Recipient */}
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                  <FiGift className="w-4 h-4" /> Destinataire
                </h4>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="font-medium">{selectedRequest.recipient_name}</div>
                  {selectedRequest.card_code && (
                    <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Code promo à usage unique:</span>
                        <span className="font-mono font-bold text-pink-600 bg-pink-100 px-2 py-1 rounded">
                          {selectedRequest.card_code}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 bg-amber-50 p-2 rounded border border-amber-200">
                        🎟️ Ce code permet une réduction de <strong>{selectedRequest.amount}€</strong> sur une réservation
                      </div>
                    </div>
                  )}
                  {selectedRequest.sent_at && (
                    <div className="text-xs text-green-600 mt-2 flex items-center gap-1">
                      <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                      Envoyée le {formatDate(selectedRequest.sent_at)} à {selectedRequest.recipient_email_sent}
                    </div>
                  )}
                </div>
              </div>

              {/* Event Date */}
              {selectedRequest.event_date && (
                <div className="flex items-center gap-2 text-gray-600">
                  <FiCalendar className="w-4 h-4" />
                  <span>Date de l'événement: {new Date(selectedRequest.event_date).toLocaleDateString('fr-FR')}</span>
                </div>
              )}

              {/* Message */}
              {selectedRequest.message && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                    <FiMessageSquare className="w-4 h-4" /> Message
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-3 text-gray-600 italic">
                    "{selectedRequest.message}"
                  </div>
                </div>
              )}

              {/* Send Card Section - Only show if status is 'paid' and not yet sent */}
              {selectedRequest.status === 'paid' && !selectedRequest.card_code && (
                <div className="space-y-3 pt-4 border-t bg-gradient-to-r from-pink-50 to-purple-50 -mx-6 px-6 pb-4 rounded-b-lg">
                  <h4 className="font-semibold text-pink-700 flex items-center gap-2">
                    <FiSend className="w-4 h-4" /> Envoyer la carte cadeau digitale
                  </h4>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="Email du destinataire"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      onClick={() => sendGiftCard(selectedRequest.id)}
                      disabled={!recipientEmail || sendingCard}
                      className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
                    >
                      {sendingCard ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                          Envoi...
                        </>
                      ) : (
                        <>
                          <FiSend className="w-4 h-4 mr-2" />
                          Envoyer
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Le destinataire recevra un email avec sa carte cadeau digitale personnalisée.
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <span className="text-sm text-gray-500 mr-2">Changer le statut:</span>
                {['pending', 'contacted', 'paid', 'sent', 'cancelled'].map(status => (
                  <Button
                    key={status}
                    size="sm"
                    variant={selectedRequest.status === status ? 'default' : 'outline'}
                    onClick={() => updateStatus(selectedRequest.id, status)}
                    className={selectedRequest.status === status ? 'bg-pink-500 hover:bg-pink-600' : ''}
                  >
                    {statusConfig[status]?.label}
                  </Button>
                ))}
              </div>

              <div className="flex justify-between pt-2">
                <Button
                  variant="outline"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => deleteRequest(selectedRequest.id)}
                >
                  <FiTrash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
                <Button onClick={() => setShowDetailModal(false)}>
                  Fermer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GiftCardRequestsPage;
